import { useState } from "react";
import { Lock, Check, Loader2, Ban } from "lucide-react";
import { toast } from "sonner";
import { formatErr, imgSrc } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LEVELS, levelFor } from "@/lib/levels";
import { useLevelFrame, wearFrame } from "@/lib/levelFrames";
import { useGrowth } from "@/lib/growth";

export const FrameAvatar = ({ level, name, picture, size = "w-14 h-14" }) => (
  <span className={`${size} relative rounded-full p-[3px] shrink-0 lvl-frame lvl-frame-${level} block`}>
    {picture ? <img src={imgSrc(picture)} alt="" className="w-full h-full rounded-full object-cover bg-white" />
      : <span className="w-full h-full rounded-full bg-white flex items-center justify-center font-display font-extrabold text-rose-500">{name?.[0]?.toUpperCase()}</span>}
  </span>
);

export function LevelFramePicker() {
  const { user, refreshUser } = useAuth();
  const worn = useLevelFrame(user?.user_id);
  const [busy, setBusy] = useState(null);
  const g = useGrowth();
  if (!user) return null;
  const unlocked = Math.max(user.level_reached || 0, levelFor(user.points ?? 0).level);
  const pick = async (level) => {
    setBusy(level ?? 0);
    try {
      await wearFrame(user.user_id, level);
      refreshUser();
      toast.success(level ? `Rahmen „${level > 100 ? g?.tiers.find((t) => t.frame === level)?.title : LEVELS[level - 1].name}“ angelegt ✨` : "Rahmen abgelegt");
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(null);
  };
  return (
    <div className="bg-white rounded-3xl p-6 border border-pink-100 space-y-4" data-testid="level-frame-picker">
      <div>
        <h3 className="font-bold text-lg">Level-Profilrahmen</h3>
        <p className="text-xs text-slate-500 mt-1">Jedes Level schaltet einen eigenen Rahmen frei. Wähle einen aus – er erscheint an deinem Profilbild überall in der App. Geburtstags-, Ehrenmitglieder- und Unterstützer-Rahmen haben Vorrang.</p>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        <button type="button" onClick={() => pick(null)} disabled={busy !== null} data-testid="level-frame-none" data-active={!worn || undefined}
          className={`tap relative flex flex-col items-center gap-1.5 p-2.5 rounded-2xl border ${!worn ? "border-rose-400 bg-rose-50" : "border-pink-100"}`}>
          <span className="w-14 h-14 rounded-full border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400">{busy === 0 ? <Loader2 className="w-5 h-5 animate-spin" /> : <Ban className="w-5 h-5" />}</span>
          <span className="text-[11px] font-bold text-slate-600">Kein Rahmen</span>
        </button>
        {LEVELS.map((l, i) => {
          const lv = i + 1, locked = lv > unlocked, on = worn === lv;
          return (
            <button key={l.name} type="button" onClick={() => !locked && pick(lv)} disabled={locked || busy !== null} data-testid={`level-frame-${lv}`} data-active={on || undefined} data-locked={locked || undefined}
              title={locked ? `Ab Level ${lv} (${l.min} Punkte)` : l.name}
              className={`tap relative flex flex-col items-center gap-1.5 p-2.5 rounded-2xl border ${on ? "border-rose-400 bg-rose-50" : "border-pink-100"} ${locked ? "opacity-60" : ""}`}>
              <span className={`relative ${locked ? "grayscale" : ""}`}>
                <FrameAvatar level={lv} name={user.name} picture={user.picture} />
                {locked && <span className="absolute inset-0 flex items-center justify-center"><span className="w-7 h-7 rounded-full bg-slate-900/70 text-white flex items-center justify-center"><Lock className="w-3.5 h-3.5" /></span></span>}
                {on && <span className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center ring-2 ring-white"><Check className="w-3.5 h-3.5" strokeWidth={3} /></span>}
                {busy === lv && <span className="absolute inset-0 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-rose-600" /></span>}
              </span>
              <span className="text-[11px] font-bold text-slate-700 leading-tight text-center">{l.emoji} {l.name}</span>
              <span className="text-[10px] font-semibold text-slate-400">{locked ? `ab Level ${lv}` : `Level ${lv}`}</span>
            </button>
          );
        })}
      </div>
      {g && (
        <div className="pt-2 space-y-3" data-testid="invite-frame-picker">
          <h4 className="font-bold text-sm">Einlade-Rahmen <span className="font-semibold text-slate-400">· für eingeladene Freunde</span></h4>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
            {g.tiers.map((t) => {
              const locked = !t.reached, on = worn === t.frame;
              return (
                <button key={t.frame} type="button" onClick={() => !locked && pick(t.frame)} disabled={locked || busy !== null} data-testid={`invite-frame-${t.frame}`} data-active={on || undefined} data-locked={locked || undefined}
                  title={locked ? `Ab ${t.n} eingeladenen Freunden` : t.title}
                  className={`tap relative flex flex-col items-center gap-1.5 p-2.5 rounded-2xl border ${on ? "border-rose-400 bg-rose-50" : "border-pink-100"} ${locked ? "opacity-60" : ""}`}>
                  <span className={`relative ${locked ? "grayscale" : ""}`}>
                    <FrameAvatar level={t.frame} name={user.name} picture={user.picture} />
                    {locked && <span className="absolute inset-0 flex items-center justify-center"><span className="w-7 h-7 rounded-full bg-slate-900/70 text-white flex items-center justify-center"><Lock className="w-3.5 h-3.5" /></span></span>}
                    {on && <span className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center ring-2 ring-white"><Check className="w-3.5 h-3.5" strokeWidth={3} /></span>}
                    {busy === t.frame && <span className="absolute inset-0 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-rose-600" /></span>}
                  </span>
                  <span className="text-[11px] font-bold text-slate-700 leading-tight text-center">{t.title}</span>
                  <span className="text-[10px] font-semibold text-slate-400">{t.n} {t.n === 1 ? "Freund" : "Freunde"}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
